import React from "react";
import styles from "./AutosaveIndicator.module.css";

/**
 * Autosave status indicator with error detail support
 * @param {string} connectionStatus - "connected" or "disconnected"
 * @param {string} saveStatus - "idle", "saving", "saved", "queued", or "error"
 * @param {number} pendingCount - Number of pending patches in queue
 * @param {Object} lastError - Last error object from server (optional)
 * @param {Function} onErrorClick - Callback when error indicator is clicked (optional)
 */
export const AutosaveIndicator = ({
  connectionStatus,
  saveStatus,
  pendingCount,
  lastError = null,
  onErrorClick = null,
}) => {
  const getStatusText = () => {
    if (connectionStatus === "disconnected") {
      if (pendingCount > 0) return `Offline - ${pendingCount} pending`;
      if (saveStatus === "queued") return "Offline - queued";
      return "Offline";
    }
    if (saveStatus === "saving") return "Saving...";
    if (saveStatus === "saved") return "Saved";
    if (saveStatus === "queued") return `${pendingCount} pending`;
    if (saveStatus === "error") {
      // Show summary from structured error if available
      return lastError?.summary || "Error saving";
    }
    return "Connected";
  };

  const getStatusClass = () => {
    if (connectionStatus === "disconnected") {
      if (pendingCount > 0 || saveStatus === "queued") return styles.queued;
      return styles.offline;
    }
    if (saveStatus === "saving") return styles.saving;
    if (saveStatus === "saved") return styles.saved;
    if (saveStatus === "queued") return styles.queued;
    if (saveStatus === "error") return styles.error;
    return styles.connected;
  };

  const handleClick = () => {
    if (saveStatus === "error" && onErrorClick && lastError) {
      onErrorClick(lastError);
    }
  };

  const isClickable = saveStatus === "error" && onErrorClick && lastError;

  return (
    <div
      className={`${styles.indicator} ${getStatusClass()} ${isClickable ? styles.clickable : ""}`}
      onClick={handleClick}
      role={isClickable ? "button" : undefined}
      tabIndex={isClickable ? 0 : undefined}
      onKeyDown={isClickable ? (e) => e.key === "Enter" && handleClick() : undefined}
    >
      <span className={styles.dot} />
      <span className={styles.text}>{getStatusText()}</span>
      {isClickable && (
        <span className={styles.errorHint}>(tap for details)</span>
      )}
    </div>
  );
};
